/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { RoutingService } from 'src/app/service/routing.service';
import * as moment from 'moment';
import { SafeHtmlPipe } from 'src/app/pipe/safehtml.pipe';
import { HighlightPipe } from 'src/app/pipe/highlight.pipe';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';

@Injectable({
  providedIn: 'root',
})
export class NewsFeedProcessorService {
  constructor(
    private routing: RoutingService,
    private portfolioApi: PortfolioApiService,
  ) {}

  /**
   * Process sentiment proportions for a news item
   */
  calculateSentimentProportions(news: any, params: any): void {
    const proportions = { pos: 0, neu: 0, neg: 0 };

    news.matches.forEach((item: any) => {
      if (this.isMatchingEntity(item, params)) {
        news.sentiment = item.sentiment;
        if (!item.implied && item.spans && item.spans.length > 0) {
          proportions[item.sentiment] += 1;
        }
      }
    });

    if (
      proportions.pos !== 0 ||
      proportions.neu !== 0 ||
      proportions.neg !== 0
    ) {
      const propSum = proportions.pos + proportions.neu + proportions.neg;
      news.sentiment_proportions = {
        pos: proportions.pos / propSum,
        neu: proportions.neu / propSum,
        neg: proportions.neg / propSum,
      };
    }
  }

  /**
   * Check if an entity matches the current parameters
   */
  private isMatchingEntity(item: any, params: any): boolean {
    // Direct entity match
    if (params.type + ':' + params.id === item.entity.compound_key) {
      return true;
    }

    // Portfolio entity match
    const activeFolio = this.portfolioApi.activePortfolio;
    if (
      (this.routing.isFolio() || this.routing.isHomepage()) &&
      activeFolio &&
      !params.portfolioId
    ) {
      return activeFolio.content.includes(item.entity.compound_key);
    }

    return false;
  }

  /**
   * Process events and participants for audit mode
   */
  processAuditData(news: any): void {
    let events: string[] = [];
    let participants: string[] = [];

    if (news.events && news.events.length !== 0) {
      events = Array.from(
        new Set(
          news.events.map((event: any) => {
            if (event.participants && event.participants.length !== 0) {
              event.participants.forEach((participant: any) => {
                participants.push(participant.name);
              });
            }
            return event.name;
          }),
        ),
      );
    }

    participants = Array.from(new Set(participants));

    // Process entities
    let entities = news.matches;
    if (this.routing.isFolio() || this.routing.isHomepage()) {
      const portfolioEntities = this.portfolioApi.activePortfolioContent;
      entities = entities
        .filter((item: any) =>
          portfolioEntities.includes(item.entity.compound_key),
        )
        .map((element: any) => element.entity.name);
    } else {
      entities = entities.map((element: any) => element.entity.name);
    }

    if (entities.length === 0) {
      entities = ['No entity matches'];
    }

    news['auditEvents'] = events;
    news['auditEntities'] = entities;
    news['auditParticipants'] = participants;
  }

  /**
   * Format news title with highlighting
   */
  formatNewsTitle(news: any, params: any): void {
    if (!news) {
      news.titleFormatted = '';
      return;
    }

    if (news.reduce_visibility) {
      this.formatReducedVisibilityTitle(news);
    } else if (params.newstype === 'Events' && params.eventid) {
      this.formatEventTitle(news, params);
    } else {
      this.formatStandardTitle(news, params);
    }
  }

  /**
   * Format title for reduced visibility news
   */
  private formatReducedVisibilityTitle(news: any): void {
    let title = '';
    news.matches.map((data: any) => {
      title += data.entity.alpha_id + ' ';
    });
    news.titleFormatted = title;
  }

  /**
   * Format title for event-based news
   */
  private formatEventTitle(news: any, params: any): void {
    const eventSpans = news.events.filter(
      (item: any) => item.id === params.eventid,
    );
    const firstSpan = eventSpans[0];

    if (!firstSpan) {
      news.titleFormatted =
        'Click to see text - event span unavailable due to reprocessing';
      news.hidden = true;
      return;
    }

    let title = '';
    if (firstSpan.where === 'title') {
      title = news.title
        ? news.title.substring(firstSpan.span.start, firstSpan.span.end)
        : '';
    } else if (firstSpan.where === 'content') {
      title = news.content
        ? news.content.substring(firstSpan.span.start, firstSpan.span.end)
        : '';
    }

    // Apply highlighting
    const safeHtmlPipe = new SafeHtmlPipe();
    const highlightPipe = new HighlightPipe();
    title = safeHtmlPipe.transform(title);

    const joinedSpans = this.collectHighlightSpans(news, params, firstSpan);
    title = highlightPipe.transform(title, firstSpan.where, joinedSpans);

    news.titleFormatted = title;
  }

  /**
   * Format standard news title with highlighting
   */
  private formatStandardTitle(news: any, params: any): void {
    const newsCopy = JSON.parse(JSON.stringify(news));
    let title = newsCopy.title;

    const safeHtmlPipe = new SafeHtmlPipe();
    const highlightPipe = new HighlightPipe();
    title = safeHtmlPipe.transform(title);

    const joinedSpans = this.collectHighlightSpans(newsCopy, params, null);
    title = highlightPipe.transform(title, 'title', joinedSpans);

    news.titleFormatted = title;
  }

  /**
   * Collect spans for highlighting
   */
  private collectHighlightSpans(news: any, params: any, eventSpan: any): any {
    const joinedSpans = { spans: [] };

    news.matches.forEach((item: any) => {
      if (this.shouldHighlightMatch(item, params)) {
        const spans = this.getRelevantSpans(item, eventSpan);
        if (!item.implied && spans && spans.length > 0) {
          joinedSpans.spans = joinedSpans.spans.concat(spans);
        }
      }
    });

    return joinedSpans;
  }

  /**
   * Determine if a match should be highlighted
   */
  private shouldHighlightMatch(item: any, params: any): boolean {
    // Tag match
    if (params.tag) {
      const tagactive = decodeURI(params.tag.split('|')[0]).split(':');
      if (tagactive[1] + ':' + tagactive[2] === item.entity.compound_key) {
        return true;
      }
    }

    // Participant match
    if (params.participant === item.entity.compound_key) {
      return true;
    }

    // Entity match
    return this.isMatchingEntity(item, params);
  }

  /**
   * Get relevant spans for highlighting
   */
  private getRelevantSpans(item: any, eventSpan: any): any[] {
    if (!eventSpan) {
      // Standard title highlighting
      return item.spans.map((span: any) => ({ ...span, sentiment: 'title' }));
    }

    // Event-based highlighting
    if (eventSpan.where === 'title') {
      return item.spans
        .filter((span: any) => span.location === 'title')
        .map((span: any) => ({ ...span, sentiment: 'title' }));
    } else {
      return item.spans
        .filter(
          (span: any) =>
            span.location === 'content' &&
            span.start >= eventSpan.span.start &&
            span.end <= eventSpan.span.end,
        )
        .map((span: any) => ({
          ...span,
          sentiment: 'title',
          start: span.start - eventSpan.span.start,
          end: span.end - eventSpan.span.start,
        }));
    }
  }

  /**
   * Sort newsfeed by publish time
   */
  sortNewsfeed(newsfeed: any[], newstype: string): void {
    if (newstype && newstype !== 'Relevant') {
      newsfeed.sort(
        (a, b) =>
          new Date(b.publish_time).valueOf() -
          new Date(a.publish_time).valueOf(),
      );
    }
  }

  /**
   * Apply duplicate filtering
   */
  filterDuplicates(value: any[], duplicate: boolean): any[] {
    if (!duplicate) {
      return value;
    }

    const seenDuplicateGroupIds = new Set();
    const seenTitles = new Set();
    const filtered = [];

    value.forEach((doc) => {
      if (
        !seenDuplicateGroupIds.has(doc.duplicate_group_id) &&
        (!seenTitles.has(doc.title) || !doc.title)
      ) {
        seenDuplicateGroupIds.add(doc.duplicate_group_id);
        seenTitles.add(doc.title);
        filtered.push(doc);
      }
    });

    return filtered.reverse();
  }

  /**
   * Add time frame labels to news items
   */
  addTimeFrameLabels(news: any, params: any, frametime: any): void {
    let itime = false;
    if (params.time) {
      itime = params.time.split('--')[1];
    }

    if (!params.newsday && !itime) {
      const time = moment(news.publish_time).valueOf();
      news.time = undefined;

      if (
        !frametime.today &&
        moment().subtract(1, 'days').endOf('day').valueOf() < time
      ) {
        frametime.today = false;
        news.time = 'TODAY';
      } else if (
        !frametime.twentyfourhours &&
        moment().subtract(1, 'days').endOf('day').valueOf() > time &&
        moment().subtract(1, 'days').valueOf() < time
      ) {
        frametime.twentyfourhours = false;
        news.time = '24 HOURS';
      } else if (
        !frametime.week &&
        moment().subtract(1, 'days').valueOf() > time &&
        moment().subtract(7, 'days').valueOf() < time
      ) {
        frametime.week = false;
        news.time = 'WEEK';
      } else if (
        !frametime.month &&
        moment().subtract(7, 'days').valueOf() > time &&
        moment().subtract(30, 'days').valueOf() < time
      ) {
        frametime.month = false;
        news.time = 'MONTH';
      } else if (
        !frametime.quarter &&
        moment().subtract(30, 'days').valueOf() > time &&
        moment().subtract(90, 'days').valueOf() < time
      ) {
        frametime.quarter = false;
        news.time = 'QUARTER';
      } else if (
        !frametime.year &&
        moment().subtract(90, 'days').valueOf() > time
      ) {
        frametime.year = false;
        news.time = 'YEAR';
      }

      news.hidden = false;
      if (
        params.time === '2' &&
        moment().subtract(1, 'days').valueOf() > time &&
        moment().subtract(7, 'days').valueOf() < time
      ) {
        news.hidden = true;
      }
    }
  }
}
